import React from "react";
import styled, { css } from "styled-components";
import { rgba } from "polished";
import PropTypes from "prop-types";
import {
  SortableContainer,
  SortableElement,
  arrayMove
} from "react-sortable-hoc";
import uuid from "uuid/v4";
import FullScreenModal, { CloseButton } from "./FullScreenModal";
import { flatten, get, maxBy, sortBy } from "lodash";
import { Box } from "grid-styled";
import { getTransmittalTemplate, saveTransmittalTemplate } from "../api";
import * as C from "../settings/colors";
import Button from "./Button";
import Label from "./forms/Label";
import Input from "./forms/Input";
import TransmittalField from "./forms/TransmittalField";
import TextArea from "./forms/TextArea";
import Select from "./forms/Select";
import { NativeCheckbox } from "./forms/Checkbox";
import Text from "./Text";
import { ADD } from "../icons";
import Icon from "./Icon";

const shiftPositions = (collection, oldIndex, newIndex) => {
  const reordered = arrayMove(collection, oldIndex, newIndex);
  return reordered.map((element, index) => ({
    ...element,
    position: index + 1
  }));
};

const normalize = collection =>
  collection.reduce((acc, el) => {
    acc[el.id] = el;
    return acc;
  }, {});

const TemplateWrapper = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 20px 32px;
`;

const CustomField = styled.div`
  position: relative;
  z-index: 1;
  padding: 8px;
  user-select: none;
  cursor: pointer;
  border-radius: 4px;

  &:after {
    content: "";
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: transparent;
    z-index: 2;
  }

  &.dragging-element {
    z-index: 99999;
    background: rgba(255, 255, 255, 0.9);
    box-shadow: none;
  }

  ${props =>
    props.selected &&
    css`
      background: ${rgba(C.BLUE, 0.1)};
      box-shadow: 0 0 0 1px ${rgba(C.BLUE, 0.6)};
    `};
`;

const SortableCustomField = SortableElement(props => (
  <CustomField {...props} />
));

const SortableWrapper = SortableContainer(({ children }) => (
  <div>{children}</div>
));

const NewCustomField = styled.div`
  border: 2px dashed ${C.LIGHT_GRAY};
  padding: 20px;
  margin: 1em 8px;
  text-align: center;
  font-weight: 500;
  transition: 0.2s border-color, 0.2s color;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  user-select: none;

  &:hover {
    color: ${C.BLUE};
    border-color: ${C.BLUE};
  }
`;

const EditorHeader = styled.div`
  display: flex;
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 60px;
  z-index: 2;
  box-shadow: 0 1px 12px rgba(0, 0, 0, 0.08);
  align-items: center;
`;

const EditorContent = styled.div`
  display: flex;
  position: absolute;
  top: 60px;
  right: 0;
  bottom: 0;
  left: 0;
`;

const StructureTemplate = styled.div`
  display: flex;
  position: relative;
  z-index: 1;
  padding: 8px;
  user-select: none;
  cursor: pointer;
  border-radius: 4px;

  &.dragging-element {
    z-index: 99999;
    background: rgba(255, 255, 255, 0.9);
    box-shadow: none;
  }

  ${props =>
    props.selected &&
    css`
      background: ${rgba(C.BLUE, 0.1)};
      box-shadow: 0 0 0 1px ${rgba(C.BLUE, 0.6)};
    `};
`;

const SortableStructureTemplate = SortableElement(props => (
  <StructureTemplate {...props} />
));

const StructureTemplateTitle = styled(Text)`
  font-size: 18px;
  font-weight: 500;
`;

const Sidebar = styled.div`
  flex: 0 0 380px;
  overflow-y: auto;
  padding: 20px;
  background: ${C.SIDEBAR_BG};
`;

const TitleContainer = styled.div`
  flex: 1 1 auto;
  display: flex;
  padding: 0 32px;
`;

const ActionsContainer = styled.div`
  flex: 0 0 392px;
  display: flex;
  align-items: center;
  padding: 0 8px 0 20px;
`;

const TitleInput = styled.input`
  display: block;
  width: 100%;
  color: ${C.TEXT};
  font-weight: 500;
  font-size: 24px;
  background: 0;
  border: 0;
  padding: 4px 8px;

  &:focus {
    outline: none;
    border-radius: 4px;
    box-shadow: 0 0 0 4px ${rgba(C.BLUE, 0.4)};
  }
`;

const TYPES = {
  CUSTOM_FIELD: "customField",
  STRUCTURE_TEMPLATE: "structureTemplate"
};

const FIELD_TYPES = [
  { value: "text_field", label: "Text Field" },
  { value: "dropdown", label: "Dropdown" },
  { value: "checkbox", label: "Checkbox" },
  { value: "section", label: "Section" }
];

const ALIGNMENTS = [
  { value: "Full", label: "Full" },
  { value: "Left", label: "Left" },
  { value: "Right", label: "Right" }
];

const DATA_TYPES = [
  { value: "standard", label: "Standard" },
  { value: "measurement_ln_ft", label: "Measurement in Ln. Ft." },
  { value: "measurement_sq_ft", label: "Measurement in Sq. Ft." }
];

const getMax = (collection, property, defaultValue = 0) =>
  get(maxBy(collection, property), property, defaultValue);

class TransmittalTemplateEditor extends React.Component {
  state = {
    loading: true,
    template: null,
    structureTemplates: {},
    customFields: {},
    selected: "",
    commonSortableProps: {
      distance: 5,
      lockAxis: "y",
      helperClass: "dragging-element"
    }
  };

  handleSubmit = () => {
    const { templateId } = this.props.match.params;
    const { template, structureTemplates, customFields } = this.state;

    return saveTransmittalTemplate(
      templateId && templateId !== "new" ? templateId : null,
      {
        transmittal_template: this.serialize({
          template,
          structureTemplates,
          customFields
        })
      }
    ).then(() => {
      this.props.close();
    });
  };

  handleStructureTemplateSortEnd = ({ oldIndex, newIndex }) => {
    const { template, structureTemplates } = this.state;

    const collection = this.getStructureTemplates({
      template,
      structureTemplates
    });
    const shifted = shiftPositions(collection, oldIndex, newIndex);

    this.setState({
      structureTemplates: {
        ...structureTemplates,
        ...normalize(shifted)
      }
    });
  };

  handleCustomFieldSortEnd = (structureTemplateId = null) => ({
    oldIndex,
    newIndex
  }) => {
    const { template, structureTemplates, customFields } = this.state;
    let shifted;

    if (structureTemplateId) {
      const structureTemplateFields = this.getStructureTemplateCustomFields(
        { structureTemplates, customFields },
        structureTemplateId
      );
      shifted = shiftPositions(structureTemplateFields, oldIndex, newIndex);
    } else {
      const templateFields = this.getTemplateCustomFields({
        template,
        customFields
      });
      shifted = shiftPositions(templateFields, oldIndex, newIndex);
    }

    this.setState({
      customFields: {
        ...customFields,
        ...normalize(shifted)
      }
    });
  };

  clearSelected = evt => {
    if (evt) {
      evt.preventDefault();
      evt.stopPropagation();
    }

    this.setState({ selected: "" });
  };

  addCustomField = (structureTemplateId = null) => {
    const { template, structureTemplates, customFields } = this.state;
    const id = uuid();
    let edits = {};

    const customField = {
      id,
      name: "",
      fieldType: "text_field",
      dataType: "standard",
      alignment: "full",
      choices: "",
      required: false,
      defaultValue: "",
      _isNew: true
    };

    if (structureTemplateId) {
      const structureTemplate = structureTemplates[structureTemplateId];
      if (!structureTemplate) return;

      const structureTemplateFields = this.getStructureTemplateCustomFields(
        { structureTemplates, customFields },
        structureTemplateId
      );
      customField.position = getMax(structureTemplateFields, "position") + 1;

      edits.structureTemplates = {
        ...structureTemplates,
        [structureTemplateId]: {
          ...structureTemplate,
          customFieldIds: [...structureTemplate.customFieldIds, id]
        }
      };
    } else {
      const templateFields = this.getTemplateCustomFields({
        template,
        customFields
      });
      customField.position = getMax(templateFields, "position") + 1;
      edits.template = {
        ...template,
        customFieldIds: [...template.customFieldIds, id]
      };
    }

    edits.customFields = {
      ...customFields,
      [id]: customField
    };

    this.setState({
      ...edits,
      selected: `${TYPES.CUSTOM_FIELD}:${id}`
    });
  };

  removeCustomField = id => {
    const template = { ...this.state.template };

    let parent = template;
    const structureTemplate = Object.values(this.state.structureTemplates).find(
      st => st.customFieldIds.indexOf(id) > -1
    );

    if (structureTemplate) {
      parent = structureTemplate;
    }

    const idx = parent.customFieldIds.indexOf(id);
    if (idx === -1) return;

    const newFields = [
      ...parent.customFieldIds.slice(0, idx),
      ...parent.customFieldIds.slice(idx + 1)
    ];

    if (structureTemplate) {
      return this.setState(({ structureTemplates }) => ({
        structureTemplates: {
          ...structureTemplates,
          [structureTemplate.id]: {
            ...parent,
            customFieldIds: newFields
          }
        },
        selected: ""
      }));
    }

    this.setState({
      template: {
        ...template,
        customFieldIds: newFields
      },
      selected: ""
    });
  };

  addStructureTemplate = () => {
    const { template, structureTemplates } = this.state;
    const id = uuid();
    const newStructure = {
      id,
      name: "",
      customFieldIds: [],
      position: getMax(template.structureTemplates, "position") + 1,
      _isNew: true
    };

    this.setState({
      template: {
        ...template,
        structureTemplateIds: [...template.structureTemplateIds, id]
      },
      structureTemplates: {
        ...structureTemplates,
        [id]: newStructure
      },
      selected: `${TYPES.STRUCTURE_TEMPLATE}:${id}`
    });
  };

  removeStructureTemplate = structureId => {
    const template = { ...this.state.template };
    const idx = template.structureTemplateIds.indexOf(structureId);
    if (idx === -1) return;

    const newStructures = [
      ...template.structureTemplateIds.slice(0, idx),
      ...template.structureTemplateIds.slice(idx + 1)
    ];

    this.setState({
      template: {
        ...template,
        structureTemplateIds: newStructures
      },
      selected: ""
    });
  };

  loadTransmittalTemplate = () => {
    const { templateId } = this.props.match.params;
    this.setState({ loading: true });

    if (templateId && templateId !== "new") {
      // Fetch the existing template
      getTransmittalTemplate(templateId)
        .then(transmittal_template => {
          this.setState({
            loading: false,
            ...this.deserialize(transmittal_template)
          });
        })
        .catch(err =>
          console.error("Error while fetching transmittal template", err)
        );
    } else if (templateId === "new") {
      // Create state for a new template
      this.setState({
        loading: false,
        template: {
          name: "",
          customFieldIds: [],
          structureTemplateIds: []
        }
      });
    } else {
      // We don't have anything to load
      this.setState({ loading: false, template: null });
    }
  };

  deserialize = template => {
    const deserializeCustomField = field => ({
      id: field.id,
      name: field.name,
      fieldType: field.field_type,
      dataType: field.data_type,
      position: field.position,
      alignment: field.alignment,
      key: field.key,
      choices: (field.choices || []).join("\n"),
      required: field.required,
      defaultValue: field.default_value,
      _isNew: false
    });

    return {
      template: {
        name: template.name,
        customFieldIds: template.custom_fields.map(cf => cf.id),
        structureTemplateIds: template.structure_templates.map(st => st.id)
      },
      customFields: [
        ...template.custom_fields,
        ...flatten(template.structure_templates.map(st => st.custom_fields))
      ].reduce((acc, cf) => {
        acc[cf.id] = deserializeCustomField(cf);
        return acc;
      }, {}),
      structureTemplates: template.structure_templates.reduce((acc, st) => {
        acc[st.id] = {
          id: st.id,
          name: st.name,
          position: st.position,
          customFieldIds: st.custom_fields.map(cf => cf.id)
        };
        return acc;
      }, {})
    };
  };

  serialize = state => {
    const { template } = state;

    const serializeCustomField = field => {
      const serializedField = {
        name: field.name,
        field_type: field.fieldType,
        data_type: field.dataType,
        position: field.position,
        alignment: field.alignment,
        choices: (field.choices || "")
          .split(/\r?\n/g)
          .filter(c => c.trim() !== ""),
        required: field.required,
        default_value: field.defaultValue
      };

      if (!field._isNew) serializedField.id = field.id;

      return serializedField;
    };

    const serializeStructureTemplate = st => {
      const serializedST = {
        name: st.name,
        position: st.position,
        custom_fields: this.getStructureTemplateCustomFields(state, st.id).map(
          serializeCustomField
        )
      };

      if (!st._isNew) serializedST.id = st.id;

      return serializedST;
    };

    return {
      name: template.name,
      custom_fields: this.getTemplateCustomFields(state).map(
        serializeCustomField
      ),
      structure_templates: this.getStructureTemplates(state).map(
        serializeStructureTemplate
      )
    };
  };

  handleChange = evt => {
    const { name, value } = evt.target;
    this.setState(prevState => ({
      template: {
        ...prevState.template,
        [name]: value
      }
    }));
  };

  handleResourceChange = (id, type) => evt => {
    const { name, value, checked, type: inputType } = evt.target;
    if (!name) return;

    this.setState(prevState => ({
      [type]: {
        ...(prevState[type] || {}),
        [id]: {
          ...(prevState[type][id] || {}),
          [name]: inputType && inputType === "checkbox" ? checked : value
        }
      }
    }));
  };

  handleSelectChange = (id, type) => name => value => {
    this.handleResourceChange(id, type)({
      target: {
        name,
        value
      }
    });
  };

  handleItemClick = (id, type) => evt => {
    evt.preventDefault();
    evt.stopPropagation();

    this.setState({ selected: `${type}:${id}` });
  };

  componentDidMount() {
    this.loadTransmittalTemplate();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.loading && (!this.state.loading && !this.state.template)) {
      // We don't have any data, so let's close the editor
      this.props.close();
    }
  }

  getTemplateCustomFields({ template, customFields }) {
    if (!template) return [];

    const filtered = Object.values(customFields).filter(
      cf => template.customFieldIds.indexOf(cf.id) > -1
    );
    return sortBy(filtered, "position");
  }

  getStructureTemplates({ template, structureTemplates }) {
    if (!template) return [];

    const filtered = Object.values(structureTemplates).filter(
      st => template.structureTemplateIds.indexOf(st.id) > -1
    );
    return sortBy(filtered, "position");
  }

  getStructureTemplateCustomFields(
    { structureTemplates, customFields },
    structureTemplateId
  ) {
    const structureTemplate = structureTemplates[structureTemplateId];
    if (!structureTemplate) return [];
    const filtered = Object.values(customFields).filter(
      cf => structureTemplate.customFieldIds.indexOf(cf.id) > -1
    );
    return sortBy(filtered, "position");
  }

  renderStructureTemplate = (structureTemplate, index) => {
    let labelStyles = {};
    const [selectedType, selectedId] = this.state.selected.split(":");
    const customFields = this.getStructureTemplateCustomFields(
      this.state,
      structureTemplate.id
    );
    const addCustomFieldHandler = evt => {
      evt.preventDefault();
      evt.stopPropagation();
      this.addCustomField(structureTemplate.id);
    };

    if (!structureTemplate.name || structureTemplate.name.trim() === "") {
      labelStyles.color = C.TEXT_TERTIARY;
    }

    return (
      <SortableStructureTemplate
        key={structureTemplate.id}
        index={index}
        onClick={this.handleItemClick(
          structureTemplate.id,
          TYPES.STRUCTURE_TEMPLATE
        )}
        selected={
          selectedType === TYPES.STRUCTURE_TEMPLATE &&
          selectedId === `${structureTemplate.id}`
        }
      >
        <Box flex="0 0 240px" pr={2}>
          <StructureTemplateTitle style={labelStyles}>
            {structureTemplate.name || "Untitled Structure"}
          </StructureTemplateTitle>
        </Box>
        <Box flex="1 1 auto" pl={2}>
          <SortableWrapper
            {...this.state.commonSortableProps}
            onSortEnd={this.handleCustomFieldSortEnd(structureTemplate.id)}
          >
            {customFields.map(this.renderCustomField)}
          </SortableWrapper>
          <NewCustomField onClick={addCustomFieldHandler}>
            <Icon icon={ADD} style={{ marginRight: 4 }} /> Add a field to{" "}
            {structureTemplate.name || "structure"}
          </NewCustomField>
        </Box>
      </SortableStructureTemplate>
    );
  };

  renderCustomField = (customField, index) => {
    let labelStyles = {};
    const [selectedType, selectedId] = this.state.selected.split(":");

    if (!customField.name || customField.name.trim() === "") {
      labelStyles.color = C.TEXT_TERTIARY;
    }

    return (
      <SortableCustomField
        key={customField.id}
        index={index}
        onClick={this.handleItemClick(customField.id, TYPES.CUSTOM_FIELD)}
        selected={
          selectedType === TYPES.CUSTOM_FIELD &&
          selectedId === `${customField.id}`
        }
      >
        <TransmittalField
          templateField={{
            name: customField.name,
            field_type: customField.fieldType,
            choices: customField.choices.split(/\r?\n/g).filter(c => !!c.trim())
          }}
          labelText={customField.name || "Untitled field"}
          value={customField.defaultValue || ""}
          readOnly
        />
      </SortableCustomField>
    );
  };

  renderSidebar() {
    const { selected } = this.state;
    const [type, id] = selected.split(":");

    switch (type) {
      case TYPES.STRUCTURE_TEMPLATE:
        return this.renderStructureTemplateForm(id);
      case TYPES.CUSTOM_FIELD:
        return this.renderCustomFieldForm(id);
      default:
        return <Text>Click a field on the left to edit its properties.</Text>;
    }
  }

  renderCustomFieldForm(id) {
    const customField = this.state.customFields[id];
    if (!customField) return null;
    const inputChangeHandler = this.handleResourceChange(
      customField.id,
      "customFields"
    );
    const selectChangeHandler = this.handleSelectChange(
      customField.id,
      "customFields"
    );
    let fieldTypeOptions = null;

    const dataTypeControl = (
      <Box pb={2}>
        <Label>Data Type</Label>
        <Select
          name="dataType"
          value={customField.dataType}
          onChange={selectChangeHandler("dataType")}
          options={DATA_TYPES}
        />
      </Box>
    );

    const requiredControl = (
      <Box pb={2}>
        <NativeCheckbox
          name="required"
          checked={customField.required}
          onChange={inputChangeHandler}
          labelText="Required"
        />
      </Box>
    );

    if (customField.fieldType === "dropdown") {
      const defaultValueOptions = (customField.choices || "")
        .split(/\r?\n/g)
        .filter(c => c.trim() !== "")
        .map(choice => ({ label: choice, value: choice }));
      fieldTypeOptions = (
        <React.Fragment>
          <Box pb={2}>
            <Label>Choices</Label>
            <TextArea
              name="choices"
              value={customField.choices}
              onChange={inputChangeHandler}
              rows="6"
            />
          </Box>
          <Box pb={2}>
            <Label>Default Value</Label>
            <Select
              name="defaultValue"
              value={customField.defaultValue}
              onChange={selectChangeHandler("defaultValue")}
              options={defaultValueOptions}
            />
          </Box>
          {requiredControl}
          {dataTypeControl}
        </React.Fragment>
      );
    } else if (customField.fieldType === "text_field") {
      fieldTypeOptions = (
        <React.Fragment>
          <Box pb={2}>
            <Label>Default Value</Label>
            <Input
              name="defaultValue"
              value={customField.defaultValue || ""}
              onChange={inputChangeHandler}
            />
          </Box>
          {requiredControl}
          {dataTypeControl}
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <Box pb={2}>
          <Label>Name</Label>
          <Input
            name="name"
            value={customField.name}
            onChange={inputChangeHandler}
          />
        </Box>
        <Box pb={2}>
          <Label>Field Type</Label>
          <Select
            name="fieldType"
            value={customField.fieldType}
            onChange={selectChangeHandler("fieldType")}
            options={FIELD_TYPES}
          />
        </Box>
        {fieldTypeOptions}
        <Box pb={2}>
          <Label>Alignment</Label>
          <Select
            name="alignment"
            value={customField.alignment}
            onChange={selectChangeHandler("alignment")}
            options={ALIGNMENTS}
          />
        </Box>
        <Box pt={4}>
          <Button
            variant="danger"
            onClick={() => this.removeCustomField(customField.id)}
          >
            Delete Custom Field
          </Button>
        </Box>
      </React.Fragment>
    );
  }

  renderStructureTemplateForm(id) {
    const structureTemplate = this.state.structureTemplates[id];

    if (!structureTemplate) return null;
    const changeHandler = this.handleResourceChange(
      structureTemplate.id,
      "structureTemplates"
    );

    return (
      <React.Fragment>
        <Box pb={2}>
          <Label>Name</Label>
          <Input
            name="name"
            value={structureTemplate.name}
            onChange={changeHandler}
          />
        </Box>
        <Box pt={4}>
          <Button
            variant="danger"
            onClick={() => this.removeStructureTemplate(structureTemplate.id)}
          >
            Delete Structure Template
          </Button>
        </Box>
      </React.Fragment>
    );
  }

  renderEditor() {
    const customFields = this.getTemplateCustomFields(this.state);
    const structureTemplates = this.getStructureTemplates(this.state);

    const addCustomFieldHandler = evt => {
      evt.preventDefault();
      evt.stopPropagation();
      this.addCustomField();
    };

    const addStructureTemplateHandler = evt => {
      evt.preventDefault();
      evt.stopPropagation();
      this.addStructureTemplate();
    };

    return (
      <React.Fragment>
        <EditorHeader>
          <TitleContainer>
            <TitleInput
              name="name"
              value={this.state.template.name}
              onChange={this.handleChange}
              placeholder="Transmittal Template Name"
            />
          </TitleContainer>
          <ActionsContainer>
            <Button variant="primary" onClick={this.handleSubmit}>
              Save Transmittal Template
            </Button>
            <CloseButton
              onClick={this.props.close}
              style={{ position: "static", marginLeft: "auto" }}
            />
          </ActionsContainer>
        </EditorHeader>
        <EditorContent>
          <TemplateWrapper onClick={this.clearSelected}>
            <SortableWrapper
              {...this.state.commonSortableProps}
              onSortEnd={this.handleCustomFieldSortEnd()}
            >
              {customFields.map(this.renderCustomField)}
            </SortableWrapper>
            <NewCustomField onClick={addCustomFieldHandler}>
              <Icon icon={ADD} style={{ marginRight: 4 }} /> Add a top level
              field
            </NewCustomField>
            <SortableWrapper
              {...this.state.commonSortableProps}
              onSortEnd={this.handleStructureTemplateSortEnd}
            >
              {structureTemplates.map(this.renderStructureTemplate)}
            </SortableWrapper>
            <NewCustomField onClick={addStructureTemplateHandler}>
              <Icon icon={ADD} style={{ marginRight: 4 }} /> Add a structure
            </NewCustomField>
          </TemplateWrapper>
          <Sidebar>{this.renderSidebar()}</Sidebar>
        </EditorContent>
      </React.Fragment>
    );
  }

  render() {
    const { template } = this.state;
    return (
      <FullScreenModal
        isOpen={!!template}
        close={this.props.close}
        layout={false}
        style={{ flexDirection: "column" }}
      >
        {!!template && this.renderEditor()}
      </FullScreenModal>
    );
  }

  static propTypes = {
    close: PropTypes.func.isRequired
  };
}

export default TransmittalTemplateEditor;



// WEBPACK FOOTER //
// ./src/components/TransmittalTemplateEditor.js